import { z } from 'zod';

// ---------------------------------------------------------------------------
// "Kruxit ordered" — the shared vocabulary for the item-level work-items
// orderings (issues #201/#202). The proprietary scoring is server-only; this
// contract is the axes, per-item ranks, priority bands, and area cluster the
// dashboard and the MCP surface share. Split out from the BoardItem schema so
// that file stays within its line budget.
// ---------------------------------------------------------------------------

/**
 * The "Kruxit ordered" axes — item-level orderings that answer "what should I
 * work on next?" (issues #201/#202), distinct from the relationship-section
 * orders. `kruxit-finish` leads with in-flight work closest to done;
 * `kruxit-urgent` leads with the effective severity band (a real Linear priority,
 * else the server-derived one); `kruxit-area` groups by overlapping
 * work. The ranking is computed server-side (proprietary) and shipped as
 * `BoardItem.kruxitRank`; this is just the shared vocabulary.
 */
export const KRUXIT_ORDERS = [
  'kruxit-finish',
  'kruxit-urgent',
  'kruxit-area',
] as const;
export const KruxitOrderSchema = z.enum(KRUXIT_ORDERS);
export type KruxitOrder = z.infer<typeof KruxitOrderSchema>;

/**
 * A server-assigned global rank per Kruxit axis (smaller = do sooner). Each axis
 * ranks group-primary, so a rank-sorted list has contiguous groups; the client
 * only sorts + groups by these, never recomputing the (server-only) score.
 */
export const KruxitRankSchema = z.object({
  finish: z.number().int().nonnegative(),
  urgent: z.number().int().nonnegative(),
  area: z.number().int().nonnegative(),
});
export type KruxitRank = z.infer<typeof KruxitRankSchema>;

/** Select one axis's rank without dynamic indexing (object-injection safe). */
export function kruxitRankFor(
  rank: Readonly<KruxitRank>,
  order: KruxitOrder,
): number {
  switch (order) {
    case 'kruxit-urgent': {
      return rank.urgent;
    }
    case 'kruxit-area': {
      return rank.area;
    }
    default: {
      return rank.finish;
    }
  }
}

/** Severity bands (urgent → none) — the grouping for the `kruxit-urgent` axis. */
export const PRIORITY_BANDS = [
  'urgent',
  'high',
  'medium',
  'low',
  'none',
] as const;
export const PriorityBandSchema = z.enum(PRIORITY_BANDS);
export type PriorityBand = z.infer<typeof PriorityBandSchema>;

/**
 * Map a Linear priority (1 urgent … 4 low; `null`/`0` unset) to its band. The
 * single source the server ranks by and the client/MCP group by, so a rank-ordered
 * list always groups contiguously.
 */
export function priorityBand(priority: number | null): PriorityBand {
  switch (priority) {
    case 1: {
      return 'urgent';
    }
    case 2: {
      return 'high';
    }
    case 3: {
      return 'medium';
    }
    case 4: {
      return 'low';
    }
    default: {
      return 'none';
    }
  }
}

/** The fields the priority banding reads: a real priority and/or a derived band. */
export interface PriorityBandable {
  readonly priority: number | null;
  readonly derivedPriority?: PriorityBand | undefined;
}

/**
 * The band the `kruxit-urgent` axis groups and ranks by: a real Linear priority
 * wins; otherwise the server-derived severity band; otherwise `none`. The single
 * source the server ranks by and the client/MCP group by, so they can't drift.
 */
export function effectivePriorityBand(item: PriorityBandable): PriorityBand {
  // `0` is Linear's "No priority" (unset), so it falls through to the derived band
  // rather than ranking as a real priority.
  return item.priority === null || item.priority === 0
    ? (item.derivedPriority ?? 'none')
    : priorityBand(item.priority);
}

/** The server-computed `kruxit-area` cluster an item belongs to (file overlap, topic, or repo). */
export const KruxitAreaGroupSchema = z.object({
  /** Stable contiguous-grouping key. */
  key: z.string(),
  /** Human label (e.g. "web/auth (3 PRs)", a topic, or a repo/workspace). */
  title: z.string(),
});
export type KruxitAreaGroup = z.infer<typeof KruxitAreaGroupSchema>;
